import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { UserStateMap } from "../../types/user-state";
import { fetchUserState, putUserStateItem } from "./userStateApiClient";
import { userStateQueryKey } from "./userStateQueryKeys";
import { isUserStateEmpty } from "./userStateOptimisticQuery";
import { syncLocalUserStateToRemote } from "./userStateAnonymousSync";

export interface UseUserStateQueryOptions {
  /** Chained off identity — matches the ticket's `enabled: !!user`. */
  enabled: boolean;
  /** SSR-provided user-state map (see `redux/createStore.ts`'s `applySsrStateHydration`), seeds the cache so first paint isn't a loading state. */
  initialData?: UserStateMap;
  /**
   * Resolves once this tree's restore-and-hydrate cycle has genuinely finished — see
   * `UserContextValue.waitForUserStateRestored`'s doc comment (`query/UserProvider.tsx`) for why
   * this matters even for a *read*: without it, a signed-in user's very first fetch could snapshot
   * an empty "local" cache (for the anonymous-sync check below) before restore has actually
   * populated it, even though they had real guest data. Passed as a parameter rather than read via
   * `useWaitForUserStateRestored()`'s context hook because `useUserStateQuery` is called directly
   * inside `UserProviderInner`, which is the component that *renders* `UserContext.Provider` — it
   * can't consume its own context.
   */
  waitForRestored: () => Promise<void>;
}

/**
 * Builds the query's `queryFn`. Split out from the hook (identical logic, just lifted out of the
 * closure) so it can be unit tested without rendering — this repo has no renderHook harness, see
 * `userStateOptimisticQuery.ts`'s doc comment for the same reasoning/precedent. This is also what
 * makes the guest-to-signed-in transition (TEC-404 guest mode) testable end-to-end: a test can
 * restore a real persisted guest cache via `userStatePersister.ts` into a `QueryClient`, then call
 * this exact function against it, instead of only exercising `syncLocalUserStateToRemote` in
 * isolation.
 */
export function createUserStateQueryFn(
  queryClient: QueryClient,
  waitForRestored: () => Promise<void>
): () => Promise<UserStateMap> {
  return async () => {
    // Must happen before the cache read below — see UseUserStateQueryOptions.waitForRestored's
    // doc comment above.
    await waitForRestored();

    // Snapshot whatever's in the cache *before* this fetch overwrites it — that's the "local"
    // state the anonymous-sync check below needs, mirroring `onQueryStarted`'s
    // `stateBeforeFetch` read in the RTK Query version. For a guest who just signed in, this is
    // whatever `PersistQueryClientProvider` restored from IndexedDB on this page load.
    const localBeforeFetch = queryClient.getQueryData<UserStateMap>(userStateQueryKey);
    const remote = await fetchUserState();

    const hasLocalEntries = Boolean(localBeforeFetch) && !isUserStateEmpty(localBeforeFetch!);
    if (isUserStateEmpty(remote) && hasLocalEntries) {
      return syncLocalUserStateToRemote(localBeforeFetch!, putUserStateItem);
    }

    return remote;
  };
}

/**
 * `useQuery` port of `redux/services/userStateApi.ts`'s `getUserState` endpoint — GET
 * `/api/user-state`, chained off identity via `enabled`, with the same "remote-empty-but-had-
 * local-entries" anonymous-sync behavior (see `userStateAnonymousSync.ts`). The mutation hooks
 * (`useUpdateUserStateItem`, `useDeleteUserStateItem`) write directly into this query's cache
 * entry (`userStateQueryKey`) via `queryClient.setQueryData`, so they must stay in sync with the
 * shape returned here — the full `UserStateMap`.
 */
export function useUserStateQuery({ enabled, initialData, waitForRestored }: UseUserStateQueryOptions) {
  const queryClient = useQueryClient();

  return useQuery<UserStateMap>({
    queryKey: userStateQueryKey,
    queryFn: createUserStateQueryFn(queryClient, waitForRestored),
    enabled,
    initialData,
  });
}
